import { relations, sql } from 'drizzle-orm'
import {
  boolean,
  date,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core'
import {
  codingExercise,
  sourceContent,
  systemDesignProblem,
  task,
} from './content.js'
import {
  clientInterviewSchedulingKindEnum,
  coachingClientStatusEnum,
  coachingHomeworkStatusEnum,
} from './enums.js'
import { company, problem } from './problems.js'
import { user } from './user.js'

// =============================================================================
// COACHING DASHBOARD (Admin Only)
// =============================================================================

export const coachingClient = pgTable(
  'CoachingClient',
  {
    id: uuid('id').primaryKey().defaultRandom(),

    // Every coaching client is a platform user. Uniqueness is enforced on live
    // rows only (see the partial index below) so that demoting a client, which
    // soft-deletes this row, frees the user to be promoted again later.
    userId: text('userId')
      .notNull()
      .references(() => user.id, { onDelete: 'restrict' }),

    name: text('name').notNull(),
    namePronunciation: text('namePronunciation'),
    email: text('email'),
    phone: text('phone'),
    address: text('address'),
    location: text('location'),
    timeZone: text('timeZone'),
    programmingLanguage: text('programmingLanguage'),
    discordUsername: text('discordUsername'),
    interviewingIoUsername: text('interviewingIoUsername'),
    githubProfile: text('githubProfile'),
    leetcodeProfile: text('leetcodeProfile'),
    linkedinProfile: text('linkedinProfile'),
    dropboxRecordingsLink: text('dropboxRecordingsLink'),
    notionDatabaseId: text('notionDatabaseId'), // Client's Notion task database ID
    notionTemplates: text('notionTemplates'), // JSON array of template mappings [{name, templateId, source}]
    targetRoles: text('targetRoles'),
    availabilityNotes: text('availabilityNotes'),
    notes: text('notes'),
    baselineEpochAt: timestamp('baselineEpochAt', { mode: 'date' }),
    baselineEpochSetAt: timestamp('baselineEpochSetAt', { mode: 'date' }),
    baselineEpochSetBy: text('baselineEpochSetBy'),
    baselineEpochUpdatedAt: timestamp('baselineEpochUpdatedAt', {
      mode: 'date',
    }),
    baselineEpochClearedAt: timestamp('baselineEpochClearedAt', {
      mode: 'date',
    }),
    baselineEpochClearedBy: text('baselineEpochClearedBy'),
    status: coachingClientStatusEnum('status').default('active').notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (table) => [
    // Live rows only: a soft-deleted client keeps its userId, so demotion frees
    // the user for a later re-promotion without a raw constraint error.
    uniqueIndex('CoachingClient_userId_live_uidx')
      .on(table.userId)
      .where(sql`"deletedAt" IS NULL`),
  ],
)

export const coachingQuestionHistory = pgTable(
  'CoachingQuestionHistory',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    doneBefore: boolean('doneBefore').default(true).notNull(),
    lastAttemptDate: timestamp('lastAttemptDate', { mode: 'date' }),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),

    clientId: uuid('clientId')
      .notNull()
      .references(() => coachingClient.id, { onDelete: 'cascade' }),
    problemId: uuid('problemId')
      .notNull()
      .references(() => problem.id, { onDelete: 'cascade' }),
  },
  (table) => [
    unique().on(table.clientId, table.problemId),
    index('CoachingQuestionHistory_clientId_idx').on(table.clientId),
  ],
)

export const coachingQuestionHistoryRelations = relations(
  coachingQuestionHistory,
  ({ one }) => ({
    client: one(coachingClient, {
      fields: [coachingQuestionHistory.clientId],
      references: [coachingClient.id],
    }),
    problem: one(problem, {
      fields: [coachingQuestionHistory.problemId],
      references: [problem.id],
    }),
  }),
)

// Shared fields for all homework types
const homeworkBaseFields = {
  id: uuid('id').primaryKey().defaultRandom(),
  // Cascade matches every other CoachingClient child (LG-1007). These six were
  // the schema's only clientId columns with no constraint at all, so deleting a
  // client stranded its homework silently — 369 such rows reached production
  // before LG-1006's backfill swept them.
  //
  // AssignmentExternalDelivery holds ON DELETE restrict FKs to all six of these
  // tables, so it is a grandchild that outranks this cascade: deleting a client
  // whose homework has been delivered externally now aborts with 23503 instead
  // of cascading. That is the intended outcome — a delivery record must not lose
  // the assignment it describes — and it is loud rather than silent.
  clientId: uuid('clientId')
    .notNull()
    .references(() => coachingClient.id, { onDelete: 'cascade' }),
  dateAssigned: timestamp('dateAssigned', { mode: 'date' })
    .defaultNow()
    .notNull(),
  status: coachingHomeworkStatusEnum('status').default('active').notNull(),
  notes: text('notes'),
  notionPageId: text('notionPageId'),
  notionTemplateId: text('notionTemplateId'),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
  updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
}

export const coachingHomeworkProblem = pgTable(
  'CoachingHomeworkProblem',
  {
    ...homeworkBaseFields,
    problemId: uuid('problemId').notNull(),
  },
  (t) => [
    index('CoachingHomeworkProblem_clientId_idx').on(t.clientId),
    index('CoachingHomeworkProblem_problemId_idx').on(t.problemId),
  ],
)

export const coachingHomeworkExercise = pgTable(
  'CoachingHomeworkExercise',
  {
    ...homeworkBaseFields,
    exerciseId: uuid('exerciseId').notNull(),
  },
  (t) => [
    index('CoachingHomeworkExercise_clientId_idx').on(t.clientId),
    index('CoachingHomeworkExercise_exerciseId_idx').on(t.exerciseId),
  ],
)

export const coachingHomeworkSystemDesign = pgTable(
  'CoachingHomeworkSystemDesign',
  {
    ...homeworkBaseFields,
    systemDesignProblemId: uuid('systemDesignProblemId').notNull(),
  },
  (t) => [
    index('CoachingHomeworkSystemDesign_clientId_idx').on(t.clientId),
    index('CoachingHomeworkSystemDesign_systemDesignProblemId_idx').on(
      t.systemDesignProblemId,
    ),
  ],
)

export const coachingHomeworkTask = pgTable(
  'CoachingHomeworkTask',
  {
    ...homeworkBaseFields,
    taskId: uuid('taskId').notNull(),
  },
  (t) => [
    index('CoachingHomeworkTask_clientId_idx').on(t.clientId),
    index('CoachingHomeworkTask_taskId_idx').on(t.taskId),
  ],
)

export const coachingHomeworkSourceContent = pgTable(
  'CoachingHomeworkSourceContent',
  {
    ...homeworkBaseFields,
    sourceContentId: uuid('sourceContentId').notNull(),
  },
  (t) => [
    index('CoachingHomeworkSourceContent_clientId_idx').on(t.clientId),
    index('CoachingHomeworkSourceContent_sourceContentId_idx').on(
      t.sourceContentId,
    ),
  ],
)

export const coachingHomeworkOther = pgTable(
  'CoachingHomeworkOther',
  {
    ...homeworkBaseFields,
    title: text('title').notNull(),
    description: text('description'),
    url: text('url'),
  },
  (t) => [index('CoachingHomeworkOther_clientId_idx').on(t.clientId)],
)

// Homework relations

export const coachingHomeworkProblemRelations = relations(
  coachingHomeworkProblem,
  ({ one }) => ({
    problem: one(problem, {
      fields: [coachingHomeworkProblem.problemId],
      references: [problem.id],
    }),
    client: one(coachingClient, {
      fields: [coachingHomeworkProblem.clientId],
      references: [coachingClient.id],
    }),
  }),
)

export const coachingHomeworkExerciseRelations = relations(
  coachingHomeworkExercise,
  ({ one }) => ({
    exercise: one(codingExercise, {
      fields: [coachingHomeworkExercise.exerciseId],
      references: [codingExercise.id],
    }),
    client: one(coachingClient, {
      fields: [coachingHomeworkExercise.clientId],
      references: [coachingClient.id],
    }),
  }),
)

export const coachingHomeworkSystemDesignRelations = relations(
  coachingHomeworkSystemDesign,
  ({ one }) => ({
    systemDesignProblem: one(systemDesignProblem, {
      fields: [coachingHomeworkSystemDesign.systemDesignProblemId],
      references: [systemDesignProblem.id],
    }),
    client: one(coachingClient, {
      fields: [coachingHomeworkSystemDesign.clientId],
      references: [coachingClient.id],
    }),
  }),
)

export const coachingHomeworkTaskRelations = relations(
  coachingHomeworkTask,
  ({ one }) => ({
    task: one(task, {
      fields: [coachingHomeworkTask.taskId],
      references: [task.id],
    }),
    client: one(coachingClient, {
      fields: [coachingHomeworkTask.clientId],
      references: [coachingClient.id],
    }),
  }),
)

export const coachingHomeworkSourceContentRelations = relations(
  coachingHomeworkSourceContent,
  ({ one }) => ({
    sourceContent: one(sourceContent, {
      fields: [coachingHomeworkSourceContent.sourceContentId],
      references: [sourceContent.id],
    }),
    client: one(coachingClient, {
      fields: [coachingHomeworkSourceContent.clientId],
      references: [coachingClient.id],
    }),
  }),
)

export const coachingHomeworkOtherRelations = relations(
  coachingHomeworkOther,
  ({ one }) => ({
    client: one(coachingClient, {
      fields: [coachingHomeworkOther.clientId],
      references: [coachingClient.id],
    }),
  }),
)

export const coachingClientCheckIn = pgTable('CoachingClientCheckIn', {
  id: uuid('id').primaryKey().defaultRandom(),
  clientId: uuid('clientId')
    .notNull()
    .references(() => coachingClient.id, { onDelete: 'cascade' }),
  checkedInAt: timestamp('checkedInAt', { mode: 'date' })
    .defaultNow()
    .notNull(),
  createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
})

export const coachingClientCheckInRelations = relations(
  coachingClientCheckIn,
  ({ one }) => ({
    client: one(coachingClient, {
      fields: [coachingClientCheckIn.clientId],
      references: [coachingClient.id],
    }),
  }),
)

export const roleTrack = pgTable(
  'RoleTrack',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    slug: text('slug').notNull().unique(),
    name: text('name').notNull(),
    description: text('description'),
    sortOrder: integer('sortOrder').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [index('RoleTrack_deletedAt_idx').on(t.deletedAt)],
)

export const seniorityBand = pgTable(
  'SeniorityBand',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    slug: text('slug').notNull().unique(),
    name: text('name').notNull(),
    rank: integer('rank').default(0).notNull(),
    description: text('description'),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [index('SeniorityBand_deletedAt_idx').on(t.deletedAt)],
)

export const companyLevel = pgTable(
  'CompanyLevel',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    companyId: uuid('companyId')
      .notNull()
      .references(() => company.id),
    seniorityBandId: uuid('seniorityBandId')
      .notNull()
      .references(() => seniorityBand.id),
    label: text('label').notNull(),
    sortOrder: integer('sortOrder').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    unique('CompanyLevel_companyId_id_unique').on(t.companyId, t.id),
    uniqueIndex('CompanyLevel_companyId_label_active_uidx')
      .on(t.companyId, t.label)
      .where(sql`"deletedAt" IS NULL`),
    index('CompanyLevel_companyId_idx').on(t.companyId),
    index('CompanyLevel_seniorityBandId_idx').on(t.seniorityBandId),
    index('CompanyLevel_deletedAt_idx').on(t.deletedAt),
  ],
)

export const companyInterviewProcess = pgTable(
  'CompanyInterviewProcess',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    companyId: uuid('companyId')
      .notNull()
      .references(() => company.id),
    roleTrackId: uuid('roleTrackId')
      .notNull()
      .references(() => roleTrack.id),
    seniorityBandId: uuid('seniorityBandId')
      .notNull()
      .references(() => seniorityBand.id),
    companyLevelId: uuid('companyLevelId').references(() => companyLevel.id),
    name: text('name').notNull(),
    description: text('description').default('').notNull(),
    isDefault: boolean('isDefault').default(false).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('CompanyInterviewProcess_companyId_idx').on(t.companyId),
    index('CompanyInterviewProcess_roleTrackId_idx').on(t.roleTrackId),
    index('CompanyInterviewProcess_seniorityBandId_idx').on(t.seniorityBandId),
    index('CompanyInterviewProcess_companyLevelId_idx').on(t.companyLevelId),
    index('CompanyInterviewProcess_deletedAt_idx').on(t.deletedAt),
  ],
)

export const companyInterviewProcessStage = pgTable(
  'CompanyInterviewProcessStage',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    processId: uuid('processId')
      .notNull()
      .references(() => companyInterviewProcess.id, { onDelete: 'cascade' }),
    label: text('label').notNull(),
    description: text('description').default('').notNull(),
    stageKind: text('stageKind').notNull(),
    sortOrder: integer('sortOrder').default(0).notNull(),
    isOptional: boolean('isOptional').default(false).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('CompanyInterviewProcessStage_processId_idx').on(t.processId),
    index('CompanyInterviewProcessStage_deletedAt_idx').on(t.deletedAt),
  ],
)

export const companyInterviewProcessStageFocus = pgTable(
  'CompanyInterviewProcessStageFocus',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    stageId: uuid('stageId')
      .notNull()
      .references(() => companyInterviewProcessStage.id, {
        onDelete: 'cascade',
      }),
    label: text('label').notNull(),
    description: text('description').default('').notNull(),
    sortOrder: integer('sortOrder').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('CompanyInterviewProcessStageFocus_stageId_idx').on(t.stageId),
    index('CompanyInterviewProcessStageFocus_deletedAt_idx').on(t.deletedAt),
  ],
)

export const clientApplication = pgTable(
  'ClientApplication',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    clientId: uuid('clientId')
      .notNull()
      .references(() => coachingClient.id, { onDelete: 'cascade' }),
    companyId: uuid('companyId').references(() => company.id),
    companyNameSnapshot: text('companyNameSnapshot'),
    roleTrackId: uuid('roleTrackId').references(() => roleTrack.id),
    seniorityBandId: uuid('seniorityBandId').references(() => seniorityBand.id),
    companyLevelId: uuid('companyLevelId').references(() => companyLevel.id),
    sourceProcessId: uuid('sourceProcessId').references(
      () => companyInterviewProcess.id,
    ),
    roleTitle: text('roleTitle').default('').notNull(),
    status: text('status').default('interviewing').notNull(),
    notes: text('notes').default('').notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('ClientApplication_clientId_idx').on(t.clientId),
    index('ClientApplication_companyId_idx').on(t.companyId),
    index('ClientApplication_sourceProcessId_idx').on(t.sourceProcessId),
    index('ClientApplication_deletedAt_idx').on(t.deletedAt),
    uniqueIndex('ClientApplication_client_company_name_active_uidx')
      .on(t.clientId, sql`lower(${t.companyNameSnapshot})`)
      .where(sql`"deletedAt" IS NULL AND "companyNameSnapshot" IS NOT NULL`),
  ],
)

export const clientApplicationStage = pgTable(
  'ClientApplicationStage',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    applicationId: uuid('applicationId')
      .notNull()
      .references(() => clientApplication.id, { onDelete: 'cascade' }),
    sourceProcessStageId: uuid('sourceProcessStageId').references(
      () => companyInterviewProcessStage.id,
    ),
    label: text('label').notNull(),
    description: text('description').default('').notNull(),
    stageKind: text('stageKind').notNull(),
    sortOrder: integer('sortOrder').default(0).notNull(),
    status: text('status').default('expected').notNull(),
    isOptional: boolean('isOptional').default(false).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('ClientApplicationStage_applicationId_idx').on(t.applicationId),
    index('ClientApplicationStage_sourceStageId_idx').on(
      t.sourceProcessStageId,
    ),
    index('ClientApplicationStage_deletedAt_idx').on(t.deletedAt),
  ],
)

export const clientApplicationStageFocus = pgTable(
  'ClientApplicationStageFocus',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    applicationStageId: uuid('applicationStageId')
      .notNull()
      .references(() => clientApplicationStage.id, { onDelete: 'cascade' }),
    label: text('label').notNull(),
    sortOrder: integer('sortOrder').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('ClientApplicationStageFocus_stageId_idx').on(t.applicationStageId),
  ],
)

export const clientInterview = pgTable(
  'ClientInterview',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    applicationId: uuid('applicationId')
      .notNull()
      .references(() => clientApplication.id, { onDelete: 'cascade' }),
    applicationStageId: uuid('applicationStageId').references(
      () => clientApplicationStage.id,
    ),
    schedulingKind:
      clientInterviewSchedulingKindEnum('schedulingKind').notNull(),
    notes: text('notes').default('').notNull(),
    startDate: date('startDate', { mode: 'date' }),
    endDate: date('endDate', { mode: 'date' }),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('ClientInterview_applicationId_idx').on(t.applicationId),
    index('ClientInterview_stageId_idx').on(t.applicationStageId),
    index('ClientInterview_deletedAt_idx').on(t.deletedAt),
  ],
)

export const clientInterviewAvailabilityDate = pgTable(
  'ClientInterviewAvailabilityDate',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    interviewId: uuid('interviewId')
      .notNull()
      .references(() => clientInterview.id, { onDelete: 'cascade' }),
    availableDate: date('availableDate', { mode: 'date' }).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('ClientInterviewAvailabilityDate_interviewId_idx').on(t.interviewId),
    uniqueIndex('ClientInterviewAvailabilityDate_interview_date_active_uidx')
      .on(t.interviewId, t.availableDate)
      .where(sql`"deletedAt" IS NULL`),
  ],
)

export const clientContact = pgTable(
  'ClientContact',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    clientId: uuid('clientId')
      .notNull()
      .references(() => coachingClient.id, { onDelete: 'cascade' }),
    companyId: uuid('companyId').references(() => company.id),
    companyNameSnapshot: text('companyNameSnapshot'),
    name: text('name').notNull(),
    title: text('title').default('').notNull(),
    email: text('email').default('').notNull(),
    phone: text('phone').default('').notNull(),
    linkedInUrl: text('linkedInUrl').default('').notNull(),
    notes: text('notes').default('').notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('ClientContact_clientId_idx').on(t.clientId),
    index('ClientContact_companyId_idx').on(t.companyId),
    index('ClientContact_deletedAt_idx').on(t.deletedAt),
  ],
)

export const clientApplicationContact = pgTable(
  'ClientApplicationContact',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    applicationId: uuid('applicationId')
      .notNull()
      .references(() => clientApplication.id, { onDelete: 'cascade' }),
    contactId: uuid('contactId')
      .notNull()
      .references(() => clientContact.id, { onDelete: 'cascade' }),
    relationshipLabel: text('relationshipLabel').default('').notNull(),
    sortOrder: integer('sortOrder').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('ClientApplicationContact_applicationId_idx').on(t.applicationId),
    index('ClientApplicationContact_contactId_idx').on(t.contactId),
    uniqueIndex('ClientApplicationContact_application_contact_active_uidx')
      .on(t.applicationId, t.contactId)
      .where(sql`"deletedAt" IS NULL`),
  ],
)

export const clientInterviewContact = pgTable(
  'ClientInterviewContact',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    interviewId: uuid('interviewId')
      .notNull()
      .references(() => clientInterview.id, { onDelete: 'cascade' }),
    contactId: uuid('contactId')
      .notNull()
      .references(() => clientContact.id, { onDelete: 'cascade' }),
    roleLabel: text('roleLabel').default('').notNull(),
    sortOrder: integer('sortOrder').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [
    index('ClientInterviewContact_interviewId_idx').on(t.interviewId),
    index('ClientInterviewContact_contactId_idx').on(t.contactId),
    uniqueIndex('ClientInterviewContact_interview_contact_active_uidx')
      .on(t.interviewId, t.contactId)
      .where(sql`"deletedAt" IS NULL`),
  ],
)

export const clientInterviewLink = pgTable(
  'ClientInterviewLink',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    interviewId: uuid('interviewId')
      .notNull()
      .references(() => clientInterview.id, { onDelete: 'cascade' }),
    label: text('label').default('').notNull(),
    url: text('url').notNull(),
    sortOrder: integer('sortOrder').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [index('ClientInterviewLink_interviewId_idx').on(t.interviewId)],
)

export const clientInterviewNote = pgTable(
  'ClientInterviewNote',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    interviewId: uuid('interviewId')
      .notNull()
      .references(() => clientInterview.id, { onDelete: 'cascade' }),
    body: text('body').notNull(),
    sortOrder: integer('sortOrder').default(0).notNull(),
    createdAt: timestamp('createdAt', { mode: 'date' }).defaultNow().notNull(),
    updatedAt: timestamp('updatedAt', { mode: 'date' }).defaultNow().notNull(),
    deletedAt: timestamp('deletedAt', { mode: 'date' }),
  },
  (t) => [index('ClientInterviewNote_interviewId_idx').on(t.interviewId)],
)

export const coachingClientRelations = relations(
  coachingClient,
  ({ one, many }) => ({
    user: one(user, {
      fields: [coachingClient.userId],
      references: [user.id],
    }),
    questionHistory: many(coachingQuestionHistory),
    homeworkProblems: many(coachingHomeworkProblem),
    homeworkExercises: many(coachingHomeworkExercise),
    homeworkSystemDesign: many(coachingHomeworkSystemDesign),
    homeworkTasks: many(coachingHomeworkTask),
    homeworkSourceContent: many(coachingHomeworkSourceContent),
    homeworkOther: many(coachingHomeworkOther),
    checkIns: many(coachingClientCheckIn),
    applications: many(clientApplication),
    contacts: many(clientContact),
  }),
)

export const roleTrackRelations = relations(roleTrack, ({ many }) => ({
  processes: many(companyInterviewProcess),
  applications: many(clientApplication),
}))

export const seniorityBandRelations = relations(seniorityBand, ({ many }) => ({
  companyLevels: many(companyLevel),
  processes: many(companyInterviewProcess),
  applications: many(clientApplication),
}))

export const companyLevelRelations = relations(
  companyLevel,
  ({ one, many }) => ({
    company: one(company, {
      fields: [companyLevel.companyId],
      references: [company.id],
    }),
    seniorityBand: one(seniorityBand, {
      fields: [companyLevel.seniorityBandId],
      references: [seniorityBand.id],
    }),
    processes: many(companyInterviewProcess),
    applications: many(clientApplication),
  }),
)

export const companyInterviewProcessRelations = relations(
  companyInterviewProcess,
  ({ one, many }) => ({
    company: one(company, {
      fields: [companyInterviewProcess.companyId],
      references: [company.id],
    }),
    roleTrack: one(roleTrack, {
      fields: [companyInterviewProcess.roleTrackId],
      references: [roleTrack.id],
    }),
    seniorityBand: one(seniorityBand, {
      fields: [companyInterviewProcess.seniorityBandId],
      references: [seniorityBand.id],
    }),
    companyLevel: one(companyLevel, {
      fields: [companyInterviewProcess.companyLevelId],
      references: [companyLevel.id],
    }),
    stages: many(companyInterviewProcessStage),
    applications: many(clientApplication),
  }),
)

export const companyInterviewProcessStageRelations = relations(
  companyInterviewProcessStage,
  ({ one, many }) => ({
    process: one(companyInterviewProcess, {
      fields: [companyInterviewProcessStage.processId],
      references: [companyInterviewProcess.id],
    }),
    focuses: many(companyInterviewProcessStageFocus),
    applicationStages: many(clientApplicationStage),
  }),
)

export const companyInterviewProcessStageFocusRelations = relations(
  companyInterviewProcessStageFocus,
  ({ one }) => ({
    stage: one(companyInterviewProcessStage, {
      fields: [companyInterviewProcessStageFocus.stageId],
      references: [companyInterviewProcessStage.id],
    }),
  }),
)

export const clientApplicationRelations = relations(
  clientApplication,
  ({ one, many }) => ({
    client: one(coachingClient, {
      fields: [clientApplication.clientId],
      references: [coachingClient.id],
    }),
    company: one(company, {
      fields: [clientApplication.companyId],
      references: [company.id],
    }),
    roleTrack: one(roleTrack, {
      fields: [clientApplication.roleTrackId],
      references: [roleTrack.id],
    }),
    seniorityBand: one(seniorityBand, {
      fields: [clientApplication.seniorityBandId],
      references: [seniorityBand.id],
    }),
    companyLevel: one(companyLevel, {
      fields: [clientApplication.companyLevelId],
      references: [companyLevel.id],
    }),
    sourceProcess: one(companyInterviewProcess, {
      fields: [clientApplication.sourceProcessId],
      references: [companyInterviewProcess.id],
    }),
    stages: many(clientApplicationStage),
    interviews: many(clientInterview),
    contacts: many(clientApplicationContact),
  }),
)

export const clientApplicationStageRelations = relations(
  clientApplicationStage,
  ({ one, many }) => ({
    application: one(clientApplication, {
      fields: [clientApplicationStage.applicationId],
      references: [clientApplication.id],
    }),
    sourceProcessStage: one(companyInterviewProcessStage, {
      fields: [clientApplicationStage.sourceProcessStageId],
      references: [companyInterviewProcessStage.id],
    }),
    focuses: many(clientApplicationStageFocus),
    interviews: many(clientInterview),
  }),
)

export const clientApplicationStageFocusRelations = relations(
  clientApplicationStageFocus,
  ({ one }) => ({
    applicationStage: one(clientApplicationStage, {
      fields: [clientApplicationStageFocus.applicationStageId],
      references: [clientApplicationStage.id],
    }),
  }),
)

export const clientInterviewRelations = relations(
  clientInterview,
  ({ one, many }) => ({
    application: one(clientApplication, {
      fields: [clientInterview.applicationId],
      references: [clientApplication.id],
    }),
    applicationStage: one(clientApplicationStage, {
      fields: [clientInterview.applicationStageId],
      references: [clientApplicationStage.id],
    }),
    availabilityDates: many(clientInterviewAvailabilityDate),
    contacts: many(clientInterviewContact),
    links: many(clientInterviewLink),
    notes: many(clientInterviewNote),
  }),
)

export const clientInterviewAvailabilityDateRelations = relations(
  clientInterviewAvailabilityDate,
  ({ one }) => ({
    interview: one(clientInterview, {
      fields: [clientInterviewAvailabilityDate.interviewId],
      references: [clientInterview.id],
    }),
  }),
)

export const clientContactRelations = relations(
  clientContact,
  ({ one, many }) => ({
    client: one(coachingClient, {
      fields: [clientContact.clientId],
      references: [coachingClient.id],
    }),
    company: one(company, {
      fields: [clientContact.companyId],
      references: [company.id],
    }),
    applications: many(clientApplicationContact),
    interviews: many(clientInterviewContact),
  }),
)

export const clientApplicationContactRelations = relations(
  clientApplicationContact,
  ({ one }) => ({
    application: one(clientApplication, {
      fields: [clientApplicationContact.applicationId],
      references: [clientApplication.id],
    }),
    contact: one(clientContact, {
      fields: [clientApplicationContact.contactId],
      references: [clientContact.id],
    }),
  }),
)

export const clientInterviewContactRelations = relations(
  clientInterviewContact,
  ({ one }) => ({
    interview: one(clientInterview, {
      fields: [clientInterviewContact.interviewId],
      references: [clientInterview.id],
    }),
    contact: one(clientContact, {
      fields: [clientInterviewContact.contactId],
      references: [clientContact.id],
    }),
  }),
)

export const clientInterviewLinkRelations = relations(
  clientInterviewLink,
  ({ one }) => ({
    interview: one(clientInterview, {
      fields: [clientInterviewLink.interviewId],
      references: [clientInterview.id],
    }),
  }),
)

export const clientInterviewNoteRelations = relations(
  clientInterviewNote,
  ({ one }) => ({
    interview: one(clientInterview, {
      fields: [clientInterviewNote.interviewId],
      references: [clientInterview.id],
    }),
  }),
)

export type CoachingClient = typeof coachingClient.$inferSelect
export type NewCoachingClient = typeof coachingClient.$inferInsert
export type CoachingQuestionHistory =
  typeof coachingQuestionHistory.$inferSelect
export type NewCoachingQuestionHistory =
  typeof coachingQuestionHistory.$inferInsert
export type CoachingHomeworkProblem =
  typeof coachingHomeworkProblem.$inferSelect
export type NewCoachingHomeworkProblem =
  typeof coachingHomeworkProblem.$inferInsert

export type CoachingHomeworkExercise =
  typeof coachingHomeworkExercise.$inferSelect
export type NewCoachingHomeworkExercise =
  typeof coachingHomeworkExercise.$inferInsert

export type CoachingHomeworkSystemDesign =
  typeof coachingHomeworkSystemDesign.$inferSelect
export type NewCoachingHomeworkSystemDesign =
  typeof coachingHomeworkSystemDesign.$inferInsert

export type CoachingHomeworkTask = typeof coachingHomeworkTask.$inferSelect
export type NewCoachingHomeworkTask = typeof coachingHomeworkTask.$inferInsert

export type CoachingHomeworkSourceContent =
  typeof coachingHomeworkSourceContent.$inferSelect
export type NewCoachingHomeworkSourceContent =
  typeof coachingHomeworkSourceContent.$inferInsert

export type CoachingHomeworkOther = typeof coachingHomeworkOther.$inferSelect
export type NewCoachingHomeworkOther = typeof coachingHomeworkOther.$inferInsert

export type CoachingClientCheckIn = typeof coachingClientCheckIn.$inferSelect
export type NewCoachingClientCheckIn = typeof coachingClientCheckIn.$inferInsert

export type RoleTrack = typeof roleTrack.$inferSelect
export type NewRoleTrack = typeof roleTrack.$inferInsert
export type SeniorityBand = typeof seniorityBand.$inferSelect
export type NewSeniorityBand = typeof seniorityBand.$inferInsert
export type CompanyLevel = typeof companyLevel.$inferSelect
export type NewCompanyLevel = typeof companyLevel.$inferInsert
export type CompanyInterviewProcess =
  typeof companyInterviewProcess.$inferSelect
export type NewCompanyInterviewProcess =
  typeof companyInterviewProcess.$inferInsert
export type CompanyInterviewProcessStage =
  typeof companyInterviewProcessStage.$inferSelect
export type NewCompanyInterviewProcessStage =
  typeof companyInterviewProcessStage.$inferInsert
export type CompanyInterviewProcessStageFocus =
  typeof companyInterviewProcessStageFocus.$inferSelect
export type NewCompanyInterviewProcessStageFocus =
  typeof companyInterviewProcessStageFocus.$inferInsert
export type ClientApplication = typeof clientApplication.$inferSelect
export type NewClientApplication = typeof clientApplication.$inferInsert
export type ClientApplicationStage = typeof clientApplicationStage.$inferSelect
export type NewClientApplicationStage =
  typeof clientApplicationStage.$inferInsert
export type ClientApplicationStageFocus =
  typeof clientApplicationStageFocus.$inferSelect
export type NewClientApplicationStageFocus =
  typeof clientApplicationStageFocus.$inferInsert
export type ClientInterview = typeof clientInterview.$inferSelect
export type NewClientInterview = typeof clientInterview.$inferInsert
export type ClientInterviewAvailabilityDate =
  typeof clientInterviewAvailabilityDate.$inferSelect
export type NewClientInterviewAvailabilityDate =
  typeof clientInterviewAvailabilityDate.$inferInsert
export type ClientContact = typeof clientContact.$inferSelect
export type NewClientContact = typeof clientContact.$inferInsert
export type ClientApplicationContact =
  typeof clientApplicationContact.$inferSelect
export type NewClientApplicationContact =
  typeof clientApplicationContact.$inferInsert
export type ClientInterviewContact = typeof clientInterviewContact.$inferSelect
export type NewClientInterviewContact =
  typeof clientInterviewContact.$inferInsert
export type ClientInterviewLink = typeof clientInterviewLink.$inferSelect
export type NewClientInterviewLink = typeof clientInterviewLink.$inferInsert
export type ClientInterviewNote = typeof clientInterviewNote.$inferSelect
export type NewClientInterviewNote = typeof clientInterviewNote.$inferInsert
